import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createMediaMontageVideoSkill: Skill = {
    name: SKILL_NAMES.createMediaMontageVideo,
    description:
        'Showcase photos or video clips in a music-only highlight montage. Use for event recaps, real estate listings, and other videos where media is front and center.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Media Montage',
        preview: 'media-montage',
        frames: [
            'media-montage-1',
            'media-montage-2',
            'media-montage-3',
            'media-montage-4',
            'media-montage-5',
        ],
    },
    instructions: `# Create Media Montage Video
For turning a user's own photos and video clips into a music-driven highlight
montage — no narration, a title/subtitle open, a fast run of full-bleed and
split-screen media scenes, and a CTA close. This format has its own fixed
rules — no narration ever, a set duration for photo scenes, mandatory use of
split-screen designs, a specific kenburn-pairing rule, and a required CTA
close — rather than the more general "narration is fine if they want it" /
"vary duration to the content" approach.

## HARD RULE: every core scene is media-only
This entire recipe exists to showcase the user's media with nothing else on
screen. Every scene in the "Core: back-to-back media scenes" section below must
be a **media-only scene**: the photo or clip (or several) is the only thing visible — no
headline, no subtitle, no icon, no shape, no badge, nothing. Not "text kept
short." Not "text that matches the photo." None. This is the one rule in this
recipe that overrides everything else, including scene-design variety —
if the only media-only option available is repetitive, use it anyway rather
than reaching for a design that shows any text or graphic.

This does NOT apply to the opening title/subtitle scene or the closing CTA
scene — those two scenes are supposed to carry text. The rule is strictly
about the media scenes in between.

Category labels like "media" or "collage" in this template are a hint, not a
guarantee — some designs filed under those categories still carry a text or
icon asset (often meant for a caption or credit). Don't trust the category
name alone. For every candidate design:
1. Add it (or check it via \`${TOOL_NAMES.listSceneDesigns}\`/\`${TOOL_NAMES.getVideoState}\`) and look
   at its actual asset list.
2. If it has zero TEXT assets and zero icon/graphic assets — genuinely just
   media assets — it's media-only. Use it.
3. If every available option for the media count you need (one item vs.
   several) carries a TEXT or icon asset, pick the cleanest fullbleed one
   available and force every one of those non-media assets empty/hidden
   (blank text, no icon set) so nothing renders. Then confirm via
   \`${TOOL_NAMES.getVideoState}\` or \`${TOOL_NAMES.imageAsk}\` on that scene that nothing but the
   media is actually visible before moving to the next scene.
Do this check per scene, not once for the whole video — different media
counts (one vs. two vs. more) may pull different designs, and each one needs
its own asset-list check.

**Collage scenes are just as acceptable as full-bleed ones.** "Media-only"
describes the asset composition — zero text/icon assets — not the layout.
A COLLAGE design showing two or more items with no text or icon asset
passes the rule exactly like a full-bleed MEDIA design does. Aim for a real
mix of full-bleed and collage scenes whenever the template's collage options
support it — don't default to full-bleed-only out of caution, and don't
avoid collage because it takes an extra asset-list check. Only skip collage
entirely if this template genuinely has no collage design that passes the
media-only check.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- What the montage is of — the occasion, subject, or listing, with enough
  detail (who/where, any name) to compose a real title and subtitle
- The photos and video clips to feature — the more provided, the longer the
  video, unless capped by the duration answer
- Ideal duration — this determines how much of the provided media gets used
- Any specific brand media for the open or close

Two overrides to that skill for this format:
- **Don't ask about aspect ratio.** Inspect the uploaded media's orientation
  and set it (\`${TOOL_NAMES.createBlankVideo}\`) to match whichever orientation —
  landscape or portrait — the majority of them are, before building any scenes.
- **Don't ask about voiceover.** This format is music-only.

Show users options of templates matching the occasion and tone. Search if none of
the available ones seem an obvious fit.

**Duration budget.** The title-open and CTA-close scenes get a normal,
readable duration each (see below). Every remaining second is media-scene
budget: photo scenes take 3 seconds each, and video clips take however long
they naturally run. If more media was provided than the budget allows, pack
extra photos two-to-a-scene into split-screen designs before dropping
anything; if that's still not enough room, use the strongest/most
representative subset and tell the user how much didn't make it in —
never drop media silently.

## Narration and music
- **No voiceover, ever.** This format is music-only — add background music
  that matches the tone of what the montage is of.

## Opening: title and subtitle
- The first scene is a title design carrying the actual name of the occasion,
  subject, or listing and a real subtitle built from what the user told you —
  never a generic or placeholder title.

## Core: back-to-back photo scenes
Every scene below is media-only per the HARD RULE above — that governs which
specific design you pick before anything else in this section applies. Within
that constraint, aim for a genuine mix of full-bleed (MEDIA) and collage
(COLLAGE) scenes across the video whenever the template supports usable
collage options — collage is a first-class choice here, not a fallback.
- Search \`${TOOL_NAMES.listSceneDesigns}\` (\`narrative_thread="text"\`) under the MEDIA
  category for scenes holding exactly one photo, and under the COLLAGE
  category (top-and-bottom split, side-by-side split, and any other
  multi-image layout the template offers) for scenes holding two or more.
  Treat these category names only as where to start looking — the asset-list
  check from the HARD RULE section decides what's actually usable, not the
  category label.
- Rotate across whichever collage layout variants pass that check — don't
  lean on just one (e.g. always side-by-side) across the video.
- **Every photo scene is exactly 3 seconds** (\`${TOOL_NAMES.setSceneDuration}\`, mode
  \`manual\`, \`duration: 3000\`), no matter how many photos share it — these
  scenes carry no text, so there's no reading time to budget for.
- **Full brightness, no dimming, on both MEDIA and COLLAGE scenes.** On every
  media asset in every photo scene, set brightness to 1 (\`${TOOL_NAMES.setMedia}\`) and set
  the scene's dimmer/dimming control to its brightest (least-dimmed) setting
  — don't accept a design's darkened default on either category. The photos
  are the entire point of this format; nothing should dim them.
- **Kenburn pairing in multi-photo scenes.** When a scene holds more than one
  photo, give each a different pan/zoom animation, but keep the pair on the
  same axis, opposite directions: pan_up with pan_down, pan_left with
  pan_right, or zoom_in with zoom_out. Never mix axes (e.g. pan_left paired
  with zoom_in) — that reads as messy, not varied.
- Match orientation to design as usual: a portrait photo alone is safer in a
  COLLAGE inset-style layout that shows it in full than cropped into MEDIA's
  full-bleed frame; landscape photos suit MEDIA (single) or a COLLAGE
  side-by-side split (multiple) well. Vary which specific design plays next
  — avoid repeating the same one back-to-back.
- **Each uploaded photo appears in exactly one scene.** Never reuse a photo
  across scenes.
- Source these scenes only from the user's uploads (load \`${SKILL_NAMES.findUserMedia}\`
  for anything not already attached to the chat) — this format showcases
  their photos, not stock.

## Closing: call to action
- The last scene is a genuine call to action inviting the viewer to learn
  more — not another bare media scene, and not a generic wrap-up.
- Unlike the media scenes, this one carries text: give it a normal, readable
  duration (\`${TOOL_NAMES.setSceneDuration}\`, mode \`auto\`) rather than the fixed 3
  seconds.
- If the user gave specific brand media for the open or close, use it — pull
  from their upload, or load \`${SKILL_NAMES.findDamMedia}\` if a DAM is connected — rather
  than substituting stock.

## Styling
- The zero-dimming rule above applies to the photo scenes, which carry no
  text. The title-open and CTA-close scenes do carry text, so their designs
  may use whatever contrast treatment (background, scrim) they need to stay
  readable.
- Colors and styling come from the template or the user's brand kit
  (\`${SKILL_NAMES.applyBrandKit}\` skill) when one applies; don't hand-pick color
  overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- Every photo scene runs exactly 3 seconds, and every photo in them sits at
  full brightness with no dimming (dimmer at its brightest setting on both
  MEDIA and COLLAGE scenes).
- Every single photo scene is media-only — check each one's actual asset
  list (not just its category label): zero visible TEXT assets, zero
  visible icon/graphic assets, only the photo(s). Any scene that fails this
  is a defect, no matter how it looks in the preview.
- The video draws on both MEDIA and COLLAGE (within the media-only ones that
  passed the check above), and within COLLAGE actually rotates across layout
  variants (top-and-bottom split, side-by-side split, etc.) rather than
  repeating one.
- Every multi-photo scene's kenburn pairing is same-axis, opposite-direction
  — no mismatched pairs.
- No uploaded item appears more than once; anything left out was a
  deliberate, communicated trim, not a silent drop.
- The opening scene names the real occasion, subject, or listing with a real
  subtitle, and the closing scene is a genuine call to action to learn more.
- The aspect ratio matches the predominant orientation of the uploaded
  media.`,
};
